import { motion } from 'framer-motion';
import {
  Wrench,
  Ruler,
  Factory,
  Search,
  Sparkles,
  PenTool,
  Palette,
  Flame,
  Camera,
  Package,
  User,
  BarChart3 } from
'lucide-react';
import MachineCarousel from './MachineCarousel';

const CapabilitiesSection = () => {
  const categories = [
  {
    title: 'Fertigung',
    icon: Factory,
    color: 'primary',
    items: [
    { icon: Wrench, text: 'Frästeile 3-Achsig bis 610 x 420 mm' },
    { icon: Wrench, text: 'Frästeile 5-Achsig (3+2) bis 350 x 180 mm' },
    { icon: Ruler, text: 'CAD/CAM-Programmierung nach .STEP' },
    { icon: Factory, text: 'Einzelteile bis Kleinserien' },
    { icon: Search, text: 'Fertigungsoptimierte Beratung' }]

  },
  {
    title: 'Veredelung',
    icon: Sparkles,
    color: 'fastlane',
    items: [
    { icon: Sparkles, text: 'Oberflächenbehandlung durch Partner' },
    { icon: PenTool, text: 'Lasergravur für Bauteilbeschriftung' },
    { icon: Palette, text: 'Eloxieren, Vernickeln, Brünieren' },
    { icon: Flame, text: 'Verschiedene Härteverfahren' }]

  },
  {
    title: 'Service',
    icon: User,
    color: 'smart',
    items: [
    { icon: Camera, text: 'Fotodokumentation aller Schritte' },
    { icon: Package, text: 'Wunschversand (DHL, Spedition, eigener Service)' },
    { icon: User, text: 'Ein persönlicher Ansprechpartner' },
    { icon: BarChart3, text: 'Echtzeit-Informationen zum Stand' }]

  }];


  const getColorClasses = (color: string) => {
    switch (color) {
      case 'fastlane':
        return {
          bg: 'bg-fastlane/10',
          text: 'text-fastlane',
          border: 'border-fastlane/20'
        };
      case 'smart':
        return {
          bg: 'bg-smart/10',
          text: 'text-smart',
          border: 'border-smart/20'
        };
      default:
        return {
          bg: 'bg-primary/10',
          text: 'text-primary',
          border: 'border-primary/20'
        };
    }
  };

  return (
    <section className="py-20 lg:py-32">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16">

          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Komplettlösung aus einer Hand
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Von der CAD-Datei bis zum fertigen Bauteil -  alles bei einem Ansprechpartner
          </p>
        </motion.div>

        {/* Machine Carousel */}
        <MachineCarousel />

        {/* Besonderheiten */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12 bg-gradient-to-br from-[hsl(198,50%,36%)] to-[hsl(198,55%,28%)] rounded-2xl p-8 lg:p-10 relative overflow-hidden">

          {/* Decorative elements */}
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-sm" />
          <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full bg-white/5" />

          <h3 className="font-heading font-bold text-xl text-white mb-8 relative z-10">
            Besonderheiten unserer Fertigung
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10 items-stretch">
            {[
            {
              title: 'Einheitliche Steuerung',
              text: 'Alle Maschinen vom gleichen Hersteller – flexible Belegung mit bestehenden Programmen, ohne auf eine bestimmte Maschine warten zu müssen.'
            },
            {
              title: 'Flexible Mitarbeiter',
              text: 'Jeder Mitarbeiter ist an jedem Bearbeitungszentrum einsetzbar – maximale Flexibilität im Tagesgeschäft.'
            },
            {
              title: 'Maximale Produktionssicherheit',
              text: 'Bei Ausfall einer Maschine oder eines Mitarbeiters läuft die Fertigung ohne großen Aufwand weiter.'
            },
            {
              title: 'Einheitliches Werkzeugsystem',
              text: 'Abgeglichene Werkzeugnummern und -aufnahmen in der gesamten Fertigung. Unsere CAM-Datenbank wird fortlaufend aktualisiert.'
            }].
            map((feature, i) =>
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/15 hover:bg-white/15 transition-colors duration-300 h-full">

                <h4 className="font-heading font-bold text-white mb-2">{feature.title}</h4>
                <p className="text-white/75 text-sm leading-relaxed">{feature.text}</p>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {categories.map((category, categoryIndex) => {
            const colors = getColorClasses(category.color);
            const Icon = category.icon;

            return (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-shadow h-full">

                <div className="flex items-center gap-4 mb-8">
                  <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center`}>
                    <Icon className={`w-7 h-7 ${colors.text}`} />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-foreground">
                    {category.title}
                  </h3>
                </div>

                <div className="space-y-4">
                  {category.items.map((item, itemIndex) => {
                    const ItemIcon = item.icon;
                    return (
                      <motion.div
                        key={item.text}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: categoryIndex * 0.1 + itemIndex * 0.05 }}
                        className="flex items-start gap-3">

                        <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                          <ItemIcon className={`w-4 h-4 ${colors.text}`} />
                        </div>
                        <p className="text-foreground pt-1">{item.text}</p>
                      </motion.div>);

                  })}
                </div>
              </motion.div>);

          })}
        </div>
      </div>
    </section>);

};

export default CapabilitiesSection;